import * as React from 'react';
import { X } from 'lucide-react';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import type { PermissionEntity } from '@/types/permissions';
import { cn } from '@/lib/utils';

export interface EntityComboboxProps {
  label: string;
  options: PermissionEntity[];
  value: PermissionEntity | null;
  getOptionLabel: (entity: PermissionEntity) => string;
  onChange: (entity: PermissionEntity | null) => void;
  clearLabel: string;
}

const nextIndex = (current: number, step: number, count: number) => {
  // Modulo by zero yields NaN, which then fails every `>= 0` check and wedges
  // keyboard selection until the query is retyped.
  if (count === 0) return -1;
  if (current < 0) return step > 0 ? 0 : count - 1;
  return (current + step + count) % count;
};

/**
 * Single-select picker standing in for MUI's Autocomplete. tag-input is the
 * house combobox but it commits free text into a string array, which cannot
 * carry the entity object this form needs.
 */
const EntityCombobox: React.FC<EntityComboboxProps> = ({
  label,
  options,
  value,
  getOptionLabel,
  onChange,
  clearLabel,
}) => {
  // null means "not editing", so the field shows the selection. An empty string
  // is a real edit and must not fall back to the selected label, or the field
  // cannot be cleared by hand.
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [draft, setDraft] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const listboxId = `${React.useId()}-entities`;

  const selectedLabel = value ? getOptionLabel(value) : '';
  const text = draft ?? selectedLabel;

  const available = React.useMemo(() => {
    const needle = (draft ?? '').trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => getOptionLabel(option).toLowerCase().includes(needle));
  }, [options, draft, getOptionLabel]);

  const isOpen = open && available.length > 0;

  const select = (entity: PermissionEntity) => {
    onChange(entity);
    setDraft(null);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => nextIndex(current, event.key === 'ArrowDown' ? 1 : -1, available.length));
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    // isComposing keeps the Enter that confirms a kana→kanji conversion from
    // committing whichever row happens to be highlighted.
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      // Switching entity type swaps `options` without resetting activeIndex, so
      // the index can outrun a shorter list.
      const highlighted = isOpen && activeIndex >= 0 ? available[activeIndex] : undefined;
      if (highlighted) select(highlighted);
    }
  };

  return (
    <Field label={label}>
      {(control) => (
        <Popover open={isOpen} onOpenChange={setOpen}>
          <PopoverAnchor asChild>
            <div className="relative">
            <Input
              ref={inputRef}
              {...control}
              className={value ? 'pr-9' : undefined}
              role="combobox"
              autoComplete="off"
              aria-autocomplete="list"
              aria-expanded={isOpen}
              aria-controls={listboxId}
              aria-activedescendant={
                isOpen && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
              }
              value={text}
              onChange={(event) => {
                setDraft(event.target.value);
                setActiveIndex(-1);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              onKeyDown={handleKeyDown}
            />
            {value && (
              <button
                type="button"
                aria-label={clearLabel}
                onClick={() => {
                  onChange(null);
                  setDraft(null);
                  setActiveIndex(-1);
                }}
                className="absolute right-2 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" strokeWidth={2.2} />
              </button>
            )}
            </div>
          </PopoverAnchor>
          <PopoverContent
            align="start"
            sideOffset={6}
            onOpenAutoFocus={(event) => event.preventDefault()}
            // The input anchors the popover from outside its content, so Radix
            // would read the click that opens the list as a dismiss.
            onPointerDownOutside={(event) => {
              if (inputRef.current?.contains(event.target as Node)) event.preventDefault();
            }}
            onFocusOutside={(event) => {
              if (inputRef.current?.contains(event.target as Node)) event.preventDefault();
            }}
            className="revy-ui w-[var(--radix-popover-trigger-width)] p-0"
          >
            <div
              id={listboxId}
              role="listbox"
              className="max-h-60 overflow-y-auto p-1"
              onMouseDown={(event) => event.preventDefault()}
            >
              {available.map((option, index) => (
                <button
                  key={option.id}
                  id={`${listboxId}-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={value?.id === option.id}
                  tabIndex={-1}
                  onClick={() => select(option)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={cn(
                    'revy-cjk block w-full rounded-md px-2 py-1.5 text-left text-sm leading-[1.5] transition-colors',
                    index === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent',
                  )}
                >
                  {getOptionLabel(option)}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </Field>
  );
};

export default EntityCombobox;
